import type { AttrDef, ConditionEffect, ConditionRule, RuleType } from '../model/moving-item.types';
import {
  SERVICE_TYPE_LABELS,
  SERVICE_TYPE_VALUES,
  resolveBlockedServiceTypeKeys,
} from '@miso/counsel-schema';

export function isRangeAnd(rule: ConditionRule): boolean {
  if (rule.type !== 'AND' || !rule.rules || rule.rules.length !== 2) return false;
  const types = new Set(rule.rules.map((r) => r.type));
  return types.has('NUMBER_GTE') && types.has('NUMBER_LT');
}

function attrLabel(key: string, def: AttrDef | undefined): string {
  if (!def) return key.trim() || '(키 없음)';
  return (def.name?.trim() || def.key || key).trim();
}

const NUMBER_OP: Partial<Record<RuleType, string>> = {
  NUMBER_GTE: '≥',
  NUMBER_GT: '>',
  NUMBER_LTE: '≤',
  NUMBER_LT: '<',
};

function leafRuleShort(rule: ConditionRule, def: AttrDef): string {
  const name = attrLabel(def.key, def);

  if (rule.type === 'ALWAYS_MATCH') return `${name}: 항상`;
  if (rule.type === 'NEVER_MATCH') return `${name}: 절대 아님`;

  if (rule.type.startsWith('NUMBER_')) {
    const op = NUMBER_OP[rule.type] ?? rule.type;
    const t = rule.threshold ?? '?';
    return `${name} ${op} ${t}`;
  }
  if (rule.type === 'BOOLEAN_EQ') {
    return `${name} = ${rule.value ? '참' : '거짓'}`;
  }
  if (rule.type === 'ENUM_IN') {
    const vs = rule.values?.length ? rule.values.join(',') : '(없음)';
    return `${name} ∈ {${vs}}`;
  }
  return `${name}: ${rule.type}`;
}

/** 단일 leaf 규칙을 짧은 문자열로 (CROSS_AND 절 등) */
export function leafRuleToText(
  rule: ConditionRule,
  attrKey: string,
  allAttrDefs: AttrDef[]
): string {
  const def = allAttrDefs.find((a) => a.key === attrKey);
  if (!def) return `${attrKey}: …`;
  return leafRuleShort(rule, def);
}

/**
 * 조건 규칙을 칩/요약용 한 줄 문자열로
 */
export function ruleToText(
  rule: ConditionRule,
  ownerAttrDef: AttrDef,
  allAttrDefs: AttrDef[]
): string {
  if (rule.type === 'CROSS_AND' && rule.clauses?.length) {
    return rule.clauses.map((c) => leafRuleToText(c.rule, c.key, allAttrDefs)).join(' & ');
  }

  if (rule.type === 'AND' && rule.rules?.length) {
    if (isRangeAnd(rule)) {
      const gte = rule.rules.find((r) => r.type === 'NUMBER_GTE');
      const lt = rule.rules.find((r) => r.type === 'NUMBER_LT');
      const name = attrLabel(ownerAttrDef.key, ownerAttrDef);
      const min = gte?.threshold ?? '?';
      const max = lt?.threshold ?? '?';
      return `${name} ${min}~${max} (범위)`;
    }
    return rule.rules.map((r) => leafRuleShort(r, ownerAttrDef)).join(' & ');
  }

  return leafRuleShort(rule, ownerAttrDef);
}

/**
 * 효과를 칩/요약용 문자열로
 */
export function effectToText(effect: ConditionEffect, maxLen = 48): string {
  switch (effect.type) {
    case 'GUIDE_MESSAGE': {
      const raw = effect.message?.trim() ?? '';
      if (!raw) return '가이드: (메시지 없음)';
      const cut = raw.length > maxLen ? `${raw.slice(0, maxLen)}…` : raw;
      return `가이드: "${cut}"`;
    }
    case 'OPTION_HINT': {
      const k = effect.optionKey ?? '';
      const l = effect.label ?? '';
      return `옵션 힌트: ${k}${l ? ` (${l})` : ''}`;
    }
    case 'SERVICE_TYPE_CONSTRAINT': {
      // 편집기와 같은 방향(체크 = 불가)으로 읽는다
      const blocked = resolveBlockedServiceTypeKeys(effect);
      if (blocked.length === 0) return '서비스 제한: 없음';
      if (blocked.length === SERVICE_TYPE_VALUES.length) return '서비스 제한: 전부 불가';
      const labels = blocked.map((k) => SERVICE_TYPE_LABELS[k] ?? k);
      return `서비스 제한: ${labels.join('·')} 불가`;
    }
    default:
      return String(effect.type);
  }
}
